<template>
    <div class="container">
        <a-card title="Personalização">
            <fieldset class="custom-fieldset">
                <legend>Tamanho dos ícones na página Inicial</legend>

                <div class="icon-size-row">
                    <a-button class="size-btn" @click="decreaseIconSize" :disabled="iconSize <= minSize">
                        <template #icon>
                            <MinusOutlined />
                        </template>
                    </a-button>

                    <a-button class="size-btn" @click="increaseIconSize" :disabled="iconSize >= maxSize">
                        <template #icon>
                            <PlusOutlined />
                        </template>
                    </a-button>

                    <span
                        class="map-icon-btn icon-preview-wrap"
                        title="Exemplo de ícone"
                        :style="previewBoxStyle"
                    >
                        <SettingOutlined
                            class="menu-icon preview-icon"
                            :style="{ fontSize: `${previewGlyphSize}px` }"
                        />
                    </span>
                </div>

                <div class="actions-row">
                    <a-button type="primary" :loading="savingIconSize" @click="saveIconSize">
                        Salvar
                    </a-button>
                </div>
            </fieldset>

            <fieldset class="custom-fieldset map-defaults-fieldset">
                <legend>Centro e zoom iniciais dos mapas (EPSG:3857)</legend>

                <a-form layout="vertical" class="map-defaults-form">
                    <a-row :gutter="16">
                        <a-col :span="8">
                            <a-form-item label="Coordenada X (centro)">
                                <a-input-number
                                    v-model:value="mapCenterX"
                                    class="full-width"
                                    :step="100"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="8">
                            <a-form-item label="Coordenada Y (centro)">
                                <a-input-number
                                    v-model:value="mapCenterY"
                                    class="full-width"
                                    :step="100"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="8">
                            <a-form-item label="Zoom inicial">
                                <a-input-number
                                    v-model:value="mapZoom"
                                    class="full-width"
                                    :min="1"
                                    :max="22"
                                    :step="1"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>
                </a-form>

                <div class="actions-row">
                    <a-button type="primary" :loading="savingMapDefaults" @click="saveMapDefaults">
                        Salvar
                    </a-button>
                </div>
            </fieldset>

            <div class="fieldset-grid">
                <fieldset class="custom-fieldset login-background-fieldset">
                    <legend>Imagem de fundo do login</legend>

                    <div class="media-preview login-preview" :style="loginPreviewStyle" />

                    <a-upload
                        :show-upload-list="false"
                        accept="image/jpeg,image/png,image/webp"
                        :before-upload="beforeLoginBackgroundUpload"
                    >
                        <a-button :loading="uploadingLoginBackground">
                            Selecionar imagem
                        </a-button>
                    </a-upload>
                    <p class="hint-text">Formatos: JPG, PNG ou WebP. Tamanho máximo: 5 MB.</p>
                </fieldset>

                <fieldset class="custom-fieldset home-basemap-fieldset">
                    <legend>Camada de base da home</legend>

                    <div class="basemap-content">
                        <div id="map" class="map media-preview basemap-preview"></div>

                        <a-select
                            ref="select"
                            v-if="baseLayerInfo.basemapSource"
                            v-model:value="baseLayerInfo.basemapSource"
                            @change="selectBaseMap"
                            class="basemap-select"
                        >
                            <a-select-option value="none">Nenhum</a-select-option>
                            <a-select-option value="g_road_wo_building">Google Ruas sem construção</a-select-option>
                            <a-select-option value="g_road">Google Ruas</a-select-option>
                            <a-select-option value="g_sat">Google Satélite</a-select-option>
                            <a-select-option value="g_sat_e_roads">Google Satélite e Ruas</a-select-option>
                            <a-select-option value="g_terrain">Google Terreno</a-select-option>
                            <a-select-option value="g_terrain_e_roads">Google Terreno e Ruas</a-select-option>
                            <a-select-option value="esri_sat">ESRI Satélite</a-select-option>
                            <a-select-option value="carto_light">CartoDB Claro</a-select-option>
                            <a-select-option value="osm">OpenStreetMap (Ruas)</a-select-option>
                        </a-select>
                    </div>
                </fieldset>
            </div>
        </a-card>
    </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue'
import {MinusOutlined, PlusOutlined, SettingOutlined} from '@ant-design/icons-vue'
import {notification} from 'ant-design-vue'
import Map from "ol/Map";
import View from "ol/View";
import {getDefaultMapView} from "../../../../open-layers/map-defaults";
import {defaults as defaultControlsOL} from "ol/control/defaults";
import {Tile} from "ol/layer";
import XYZ from "ol/source/XYZ";
import OSM from "ol/source/OSM";
import {openNotification} from "../../../../utils/utilities";

const minSize = 16
const maxSize = 48
const iconSize = ref(32)
const savingIconSize = ref(false)

const mapCenterX = ref(-5582481.3)
const mapCenterY = ref(-3505987.0)
const mapZoom = ref(14)
const savingMapDefaults = ref(false)
const mapRef = ref(null)
const baseLayerInfo = ref({})
const basemapLayer = new Tile({source: null, properties: { baseLayerId: 'g_road_wo_building' },})
const basemapSources = {
    'g_road_wo_building': new XYZ({url: 'http://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}', crossOrigin: 'anonymous', maxZoom: 19}),
    'g_road': new XYZ({url: 'http://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', crossOrigin: 'anonymous', maxZoom: 19}),
    'g_sat': new XYZ({url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', crossOrigin: 'anonymous', maxZoom: 20}),
    'g_sat_e_roads': new XYZ({url: 'http://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', crossOrigin: 'anonymous', maxZoom: 19}),
    'g_terrain': new XYZ({url: 'http://mt1.google.com/vt/lyrs=t&x={x}&y={y}&z={z}', crossOrigin: 'anonymous', maxZoom: 19}),
    'g_terrain_e_roads': new XYZ({url: 'http://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}', crossOrigin: 'anonymous', maxZoom: 19}),
    'esri_sat': new XYZ({url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', crossOrigin: 'anonymous', maxZoom: 19}),
    'carto_light': new XYZ({url: 'https://cartodb-basemaps-a.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png', crossOrigin: 'anonymous', maxZoom: 19}),
    'osm': new OSM(),
};

const loginBackground = ref('/img/background-login.jpg')
const uploadingLoginBackground = ref(false)

const previewBoxStyle = computed(() => {
    const s = iconSize.value
    return {
        width: `${s}px`,
        height: `${s}px`,
        borderRadius: `${Math.max(4, Math.round((s / 32) * 6))}px`,
    }
})

const previewGlyphSize = computed(() => Math.round(iconSize.value * 0.5))

const loginPreviewStyle = computed(() => ({
    backgroundImage: `url('${loginBackground.value}')`,
}))

function increaseIconSize() {
    if (iconSize.value < maxSize) {
        iconSize.value += 2
    }
}

function decreaseIconSize() {
    if (iconSize.value > minSize) {
        iconSize.value -= 2
    }
}

function applyMapDefaults(view) {
    const center = view?.center
    if (Array.isArray(center) && center.length >= 2) {
        mapCenterX.value = Number(center[0])
        mapCenterY.value = Number(center[1])
    }
    const zoom = Number(view?.zoom)
    if (Number.isFinite(zoom)) {
        mapZoom.value = zoom
    }
}

async function loadPersonalization() {
    try {
        const { data } = await window.axios.get('/api/configurations/personalizations')
        const payload = data?.data ?? {}

        const icon = Number(payload.home_icon_size)
        if (Number.isFinite(icon)) {
            iconSize.value = Math.min(maxSize, Math.max(minSize, icon))
        }

        applyMapDefaults(payload.map_default_view)

        if (payload.login_background) {
            loginBackground.value = payload.login_background
        }
    } catch (error) {
        notification.error({
            message: 'Erro ao carregar personalização',
            description: error?.response?.data?.message || error.message,
        })
    }
}

async function saveIconSize() {
    try {
        savingIconSize.value = true
        const { data } = await window.axios.put('/api/configurations/personalizations', {
            type: 'home_icon_size',
            value: iconSize.value,
        })

        const savedValue = Number(data?.data?.home_icon_size)
        if (Number.isFinite(savedValue)) {
            iconSize.value = Math.min(maxSize, Math.max(minSize, savedValue))
        }

        notification.success({ message: 'Tamanho dos ícones salvo com sucesso!' })
    } catch (error) {
        notification.error({
            message: 'Erro ao salvar tamanho dos ícones',
            description: error?.response?.data?.message || error.message,
        })
    } finally {
        savingIconSize.value = false
    }
}

async function saveMapDefaults() {
    try {
        savingMapDefaults.value = true
        const { data } = await window.axios.put('/api/configurations/personalizations', {
            type: 'map_default_view',
            center_x: mapCenterX.value,
            center_y: mapCenterY.value,
            zoom: mapZoom.value,
        })

        applyMapDefaults(data?.data?.map_default_view)

        notification.success({ message: 'Centro e zoom dos mapas salvos com sucesso!' })
    } catch (error) {
        notification.error({
            message: 'Erro ao salvar centro e zoom dos mapas',
            description: error?.response?.data?.message || error.message,
        })
    } finally {
        savingMapDefaults.value = false
    }
}

function beforeLoginBackgroundUpload(file) {
    uploadLoginBackground(file)
    return false
}

async function uploadLoginBackground(file) {
    try {
        uploadingLoginBackground.value = true
        const formData = new FormData()
        formData.append('file', file)

        const { data } = await window.axios.post(
            '/api/configurations/personalizations/login-background',
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        )

        if (data?.data?.login_background) {
            loginBackground.value = `${data.data.login_background}?t=${Date.now()}`
        }

        notification.success({ message: 'Imagem de fundo do login atualizada!' })
    } catch (error) {
        notification.error({
            message: 'Erro ao enviar imagem de fundo',
            description: error?.response?.data?.message || error.message,
        })
    } finally {
        uploadingLoginBackground.value = false
    }
}

const selectBaseMap = async (e) => {
    await window.axios.put(`/api/maps/modules/${baseLayerInfo.value.id}`, { 'basemapSource': e })

    basemapLayer.setSource(basemapSources[e])
    openNotification('success', 'Mapa de base da home atualizado com sucesso!')
}

onMounted(async () => {
    mapRef.value = new Map({
        target: 'map',
        layers: [basemapLayer],
        view: new View(getDefaultMapView(12)),
        controls: defaultControlsOL({rotate: false, zoom: false})
    })

    baseLayerInfo.value = (await window.axios.get(`/api/maps/modules/get-module`, {
        params: { path: '/home' }
    })).data.data

    basemapLayer.setSource(basemapSources[baseLayerInfo.value.basemapSource])

    await loadPersonalization()
})
</script>

<style scoped>
.container {
    padding: 16px;
}

.custom-fieldset {
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
}

.custom-fieldset legend {
    padding: 0 8px;
    font-weight: 600;
}

.icon-size-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.actions-row {
    margin-top: 12px;
}

.size-btn {
    width: 32px;
    height: 32px;
    padding: 0;
}

.map-icon-btn {
    box-sizing: border-box;
    min-width: 16px;
    min-height: 16px;
    border-radius: 6px;
    background-color: rgba(0, 60, 136, 0.6);
    color: white;
    border: 2px solid white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.icon-preview-wrap {
    cursor: default;
}

.menu-icon {
    color: white;
}

.preview-icon {
    line-height: 1;
}

.full-width {
    width: 100%;
}

.fieldset-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    align-items: stretch;
}

.login-background-fieldset,
.home-basemap-fieldset {
    min-width: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
}

.media-preview {
    width: 100%;
    aspect-ratio: 16 / 9;
    height: auto;
    margin-bottom: 12px;
    border-radius: 8px;
    border: 1px solid #d9d9d9;
    overflow: hidden;
    display: block;
    box-sizing: border-box;
}

.login-preview {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
}

.basemap-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    flex: 1;
}

.basemap-preview {
    flex: 0 0 auto;
}

.basemap-select {
    width: 100%;
}

.hint-text {
    margin-top: 8px;
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
}

@media (max-width: 992px) {
    .fieldset-grid {
        grid-template-columns: 1fr;
    }
}
</style>
